import {createSelector} from '@ngrx/store';
import VideoState from '../video.state';
import {selectOverlays} from './overlay.selector';
import {VideoStateEnum} from '../../../../../enums/video-state.enum';

export const selectState = (state: { video: VideoState }) => state;
export const selectActualState = (state: { video: VideoState }) => state.video.actualVideoState;
export const selectIsInMobilePreviewModeFromStore = (state: { video: VideoState }) => state.video.isInMobilePreviewMode;
export const selectNotAvailableResolutionFromStore = (state: { video: VideoState }) => state.video.notAvailableResolution;
export const selectPreviewModeFromStore = (state: { video: VideoState }) => state.video.preview;
export const selectIsIOSFullScreenDisableFromStore = (state: { video: VideoState }) => state.video.isIOSFullScreenDisable;

export const selectVideoClipsFromState = createSelector(
    selectState,
    (state: { video: VideoState }) => state.video.videoClips);

export const selectCurrentTimeFromState = createSelector(
    selectState,
    (state: { video: VideoState }) => state.video.currentVideoTime);

export const selectCurrentFrameFromState = createSelector(
    selectState,
    (state: { video: VideoState }) => state.video.currentVideoFrame);

export const selectCurrentSceneFromState = createSelector(
    selectState,
    (state: { video: VideoState }) => state.video.currentScene);

export const selectCurrentSceneClipFromState = createSelector(
    selectState,
    (state: { video: VideoState }) => state.video.currentSceneClip);

export const selectIsCurrentSceneLoaded = createSelector(
    selectState,
    (state: { video: VideoState }) => state.video.isCurrentSceneLoaded
);

export const selectActualVideoState = createSelector(
    selectActualState,
    (actualState: VideoStateEnum) => actualState);

export const selectStartSceneTime = createSelector(
    selectState,
    (state: { video: VideoState }) => state.video.startSceneTime);

export const selectTotalTimeFromState = createSelector(
    selectState,
    (state: { video: VideoState }) => state.video.totalTime);

export const selectUserVolumeFromState = createSelector(
    selectState,
    (state: { video: VideoState }) => state.video.userVolume);

export const selectVideoScenesData = createSelector(
    selectState,
    (state: { video: VideoState }) => state.video.videoScenesData);

export const selectMasterVideoVolume = createSelector(
    selectState,
    (state: { video: VideoState }) => state.video.masterVideoVolume);

export const selectFormsFromState = createSelector(
    selectState,
    (state: { video: VideoState }) => state.video.forms);

export const selectFullScreenStateFromState = createSelector(
    selectState,
    (state: { video: VideoState }) => state.video.fullScreenState);

export const selectNavigateToTimeFromState = createSelector(
    selectState,
    (state: { video: VideoState }) => state.video.navigateToTime);

export const selectVideoClipResolutions = createSelector(
    selectCurrentSceneFromState,
    (clip) => {
        return clip && clip.resolutions ? Object.keys(clip.resolutions) : [];
    });

export const selectAllTimesFromState = createSelector(
    selectCurrentTimeFromState,
    selectTotalTimeFromState,
    selectNavigateToTimeFromState,
    (currentTime, totalTime, navigateToTime) => {
        return {
            currentTime, totalTime, navigateToTime
        };
    }
);

export const selectVideoClipForTimeFromState = createSelector(
    selectVideoClipsFromState,
    (videoClips, navigateTime: number) => {
        return {
            clipFound: videoClips.find(videoClip =>
                videoClip.time.start <= navigateTime
        && videoClip.time.end >= navigateTime)
        };
    }
);

export const selectButtonOnLastScene = createSelector(
    selectOverlays,
    selectTotalTimeFromState,
    (overlays, time: number) => {
        return overlays.filter(overlay => overlay.type === 'button' && overlay.time.end >= time);
    }
);

export const selectBlockTimesWithCurrentTimeFromStates = createSelector(
    selectFormsFromState,
    selectCurrentTimeFromState,
    (forms, currentTime) => {
        const breakPoints = [];
        forms.forEach((form) => {
            if (form.hasOwnProperty('fields') && !form.shown) {
                const blockTime = form.fields.reduce((previousValue, nextValue) =>
                    nextValue.time.end > previousValue && nextValue.required ? nextValue.time.end : previousValue, 0);

                if (blockTime !== 0) {
                    breakPoints.push(blockTime);
                }
            }
        });
        return {
            breakPointsList: breakPoints, currentTime
        };
    });

export const selectAudioTracksFromState = createSelector(
    selectState,
    (state: { video: VideoState }) => state.video.audioTracks);

export const selectUserResolutionFromState = createSelector(
    selectState,
    (state: { video: VideoState }) => state.video.userResolution);

export const selectTimelineVisibilityFromState = createSelector(
    selectState,
    (state: { video: VideoState }) => state.video.timelineVisibility);

export const selectResolutionMenuVisibilityFromState = createSelector(
    selectState,
    (state: { video: VideoState }) => state.video.resolutionMenuVisibility);

export const selectSpaceKeyListenerFromState = createSelector(
    selectState,
    (state: { video: VideoState }) => state.video.spaceKeyListener);

export const selectActionsFromState = createSelector(
    selectState,
    (state: { video: VideoState }) => state.video.actions
);

export const selectIsInMobilePreviewMode = createSelector(
    selectIsInMobilePreviewModeFromStore,
    (isInMobilePreviewMode) => isInMobilePreviewMode
);

export const selectPreviewMode = createSelector(
    selectPreviewModeFromStore,
    (preview) => preview
);

export const selectNotAvailableResolution = createSelector(
    selectNotAvailableResolutionFromStore,
    (notAvailable) => notAvailable
);

export const selectIsIOSFullScreenDisable = createSelector(
    selectState,
    (state: { video: VideoState }) => state.video.isIOSFullScreenDisable
);
